import { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import { apiDefaultFetcher } from '@/common/api/fetcher'
import {
  useNotificationSettingsGetApi,
  useNotificationSettingsUpdateApi
} from '@/common/api/modules/notificationSettings'
import { useSiteGetApi } from '@/common/api/modules/site'
import {
  useRetryUploadApi,
  useUploadLogDetailApi,
  useUploadLogsApi,
  useUploadManualApi
} from '@/common/api/modules/upload'
import { NotificationSetting } from '@/common/api/params/notificationSettings'
import { UploadApiPath, UploadLog } from '@/common/api/params/upload'
import { CSV_FORMATS, CsvFormat } from '@/common/app/modules/csv'
import { MASTER_GROUP_ORDER, masterTypeGroup } from '@/common/app/modules/master'
import { useRelayHealthApi, useRelayLogsApi } from '@/common/app/modules/relay'
import { useSiteId } from '@/common/app/site'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import MessageBar, { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge from '@/components/container/layout/AppTitleBadge'

// ─── Constants ───────────────────────────────────────────────────────────────
// マスタ種別の一覧・ラベルは CSV_FORMATS（common/app/modules/csv.ts）を単一ソースとして参照する
// （種別追加時の定義漏れを防ぐため、ここでは独自一覧を持たない）。

const statusToBadgeType = (status: string): BadgeType => {
  switch (status) {
    case 'completed':
      return BadgeType.SUCCESS
    case 'processing':
      return BadgeType.EMPHASIS
    case 'pending':
      return BadgeType.CAUTION
    case 'failed':
      return BadgeType.CRITICAL
    default:
      return BadgeType.DEFAULT
  }
}

const statusLabel = (status: string): string => {
  switch (status) {
    case 'completed':
      return '完了'
    case 'processing':
      return '処理中'
    case 'pending':
      return '待機中'
    case 'failed':
      return '失敗'
    default:
      return status
  }
}

const relayStatusToBadgeType = (status: string): BadgeType => {
  switch (status) {
    case 'forwarded':
      return BadgeType.SUCCESS
    case 'retrying':
      return BadgeType.CAUTION
    case 'failed':
      return BadgeType.CRITICAL
    default:
      return BadgeType.DEFAULT
  }
}

const relayStatusLabel = (status: string): string => {
  switch (status) {
    case 'forwarded':
      return '転送成功'
    case 'retrying':
      return 'リトライ中'
    case 'failed':
      return '転送失敗'
    default:
      return status
  }
}

const sourceToBadgeType = (source: string): BadgeType => {
  switch (source) {
    case 'ftp':
      return BadgeType.EMPHASIS
    case 'manual':
      return BadgeType.CAUTION
    case 'http':
      return BadgeType.SUCCESS
    default:
      return BadgeType.DEFAULT
  }
}

const SOURCE_LABELS: Record<string, string> = {
  http: 'API',
  ftp: 'FTP',
  manual: '手動'
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const RelayStatusBadge = ({ siteId: _siteId }: { siteId: number }) => {
  const { data, error } = useRelayHealthApi()
  const health =
    data ??
    (error
      ? { status: 'unknown', retry_count: 0, failed_count: 0, last_seen: null, tenants: [] }
      : null)

  if (!health) return null

  const badgeType =
    health.status === 'online'
      ? BadgeType.SUCCESS
      : health.status === 'warning'
        ? BadgeType.CAUTION
        : health.status === 'offline'
          ? BadgeType.CRITICAL
          : BadgeType.DEFAULT

  const label =
    health.status === 'online'
      ? '稼働中'
      : health.status === 'warning'
        ? '応答遅延'
        : health.status === 'offline'
          ? '未接続'
          : '確認中'

  return (
    <ItemAlign gapX={8}>
      {health.retry_count > 0 && (
        <Badge badgeType={BadgeType.CAUTION}>リトライ待ち: {health.retry_count}</Badge>
      )}
      {health.failed_count > 0 && (
        <Badge badgeType={BadgeType.CRITICAL}>失敗: {health.failed_count}</Badge>
      )}
      <Badge badgeType={badgeType}>FTPサーバー連携 {label}</Badge>
    </ItemAlign>
  )
}

const FtpEnabledBadge = ({ ftpEnabled }: { ftpEnabled: boolean | null }) => {
  if (ftpEnabled === null) return null
  return (
    <Badge badgeType={ftpEnabled ? BadgeType.SUCCESS : BadgeType.DEFAULT}>
      FTP自動取込 {ftpEnabled ? 'ON' : 'OFF'}
    </Badge>
  )
}

// ─── Notification Settings Panel ─────────────────────────────────────────────

const NotificationSettingsPanel = ({
  siteId,
  onClose
}: {
  siteId: number
  onClose: () => void
}) => {
  const { data: settingsData } = useNotificationSettingsGetApi({ site_id: siteId })
  const { trigger: triggerSave, isMutating: saving } = useNotificationSettingsUpdateApi()
  const [settings, setSettings] = useState<NotificationSetting[]>([])
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (settingsData) setSettings(settingsData)
  }, [settingsData])

  const handleToggle = (idx: number) => {
    setSettings((prev) => prev.map((s, i) => (i === idx ? { ...s, enabled: !s.enabled } : s)))
  }

  const handleEmailChange = (idx: number, email: string) => {
    setSettings((prev) => prev.map((s, i) => (i === idx ? { ...s, email } : s)))
  }

  const handleSave = async () => {
    setMessage('')
    try {
      await triggerSave({ site_id: siteId, settings })
      setMessage('保存しました')
    } catch {
      setMessage('保存に失敗しました')
    }
  }

  return (
    <Area>
      <AreaContent>
        <div className='mb-4 flex items-center justify-between'>
          <Text size={14} weight='bold'>
            FTPメール通知設定
          </Text>
          <Button color={ButtonColor.TERTIARY} onClick={onClose}>
            閉じる
          </Button>
        </div>
        <Text color={TextColor.SECONDARY} size={12}>
          FTP取込みエラー時のメール通知を、オペレーターごとにON/OFFできます。
        </Text>
        <div className='mt-4'>
          {settings.length === 0 ? (
            <Text color={TextColor.SECONDARY}>対象オペレーターがいません</Text>
          ) : (
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text>オペレーター名</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text>メールアドレス</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text>通知</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {settings.map((s, i) => (
                  <TableRow key={s.operator_id}>
                    <TableRowItem>
                      <Text>{s.name}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <input
                        type='email'
                        value={s.email}
                        onChange={(e) => handleEmailChange(i, e.target.value)}
                        placeholder='email@example.com'
                        className='border-secondary bg-surface text-size-12 text-primary focus:border-focus w-full rounded-lg border px-3 py-1.5 outline-none'
                      />
                    </TableRowItem>
                    <TableRowItem>
                      <Button
                        color={s.enabled ? ButtonColor.PRIMARY : ButtonColor.TERTIARY}
                        onClick={() => handleToggle(i)}
                      >
                        {s.enabled ? 'ON' : 'OFF'}
                      </Button>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </div>
        <div className='mt-4 flex items-center gap-3'>
          <Button
            color={ButtonColor.PRIMARY}
            disabled={saving}
            loading={saving}
            onClick={handleSave}
          >
            設定を保存
          </Button>
          {message && (
            <MessageBar
              type={message.includes('失敗') ? MessageType.CRITICAL : MessageType.SUCCESS}
              fillIn
            >
              {message}
            </MessageBar>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

// ─── Upload Tab ───────────────────────────────────────────────────────────────

type PreviewData = { headers: string[]; rows: string[][]; totalLines: number }
type UploadResult = {
  id?: number
  status?: string
  row_count?: number
  created_count?: number
  updated_count?: number
  skipped_count?: number
  // 全件置き換えで削除した行数（add-w2-ex-product-spec で追加）
  deleted_count?: number
  error_count?: number
  error_details?: { row: number; message: string }[]
  error?: string
}
type ProgressData = { processed_rows: number; row_count: number; status?: string }

const UploadTab = ({ siteId }: { siteId: number }) => {
  const { data: site } = useSiteGetApi({ site_id: siteId })

  const fileRef = useRef<HTMLInputElement>(null)
  const [masterType, setMasterType] = useState('products')
  const [fileName, setFileName] = useState('')
  const [result, setResult] = useState<UploadResult | null>(null)
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [progress, setProgress] = useState<ProgressData | null>(null)
  const { trigger: triggerUpload, isMutating: uploading } = useUploadManualApi()

  const parsePreview = (text: string) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim())
    const sep = lines[0]?.includes('\t') ? '\t' : ','
    const headers = lines[0]?.split(sep).map((h) => h.trim().replace(/^"|"$/g, '')) || []
    const rows = lines
      .slice(1, 6)
      .map((l) => l.split(sep).map((c) => c.trim().replace(/^"|"$/g, '')))
    setPreview({ headers, rows, totalLines: lines.length - 1 })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setResult(null)
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      if (text.includes('�')) {
        const reader2 = new FileReader()
        reader2.onload = (ev2) => parsePreview(ev2.target?.result as string)
        reader2.readAsText(file, 'Shift_JIS')
        return
      }
      parsePreview(text)
    }
    reader.readAsText(file, 'UTF-8')
  }

  const pollLog = async (logId: number, totalRows: number) => {
    setProgress({ processed_rows: 0, row_count: totalRows })
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 1000))
      try {
        const data = await apiDefaultFetcher<{
          logs?: { id: number; processed_rows?: number; row_count?: number; status?: string }[]
        }>([UploadApiPath.LIST_UPLOAD_LOGS, { site_id: siteId, per_page: 1 }])
        const log = data.logs?.find((l) => l.id === logId)
        if (log) {
          setProgress({
            processed_rows: log.processed_rows || 0,
            row_count: log.row_count || totalRows,
            status: log.status
          })
          if (log.status === 'completed' || log.status === 'failed') {
            setResult(log as UploadResult)
            setProgress(null)
            return
          }
        }
      } catch {
        /* ignore */
      }
    }
    setProgress(null)
  }

  const handleUpload = async () => {
    if (!fileRef.current?.files?.[0]) return
    setResult(null)
    setProgress(null)
    try {
      const data = await triggerUpload({
        site_id: siteId,
        master_type: masterType,
        file: fileRef.current.files[0]
      })
      setResult(data as unknown as UploadResult)
      if (data.status === 'pending' || data.status === 'processing') {
        pollLog(data.id!, data.row_count || preview?.totalLines || 0)
      }
    } catch (e: unknown) {
      if (e instanceof Response) {
        const errData = await e.json().catch(() => ({}))
        setResult({ error: errData.detail || 'アップロードに失敗しました' })
      } else {
        setResult({ error: e instanceof Error ? e.message : 'エラーが発生しました' })
      }
    }
  }

  const progressPct =
    progress && progress.row_count > 0
      ? Math.min(100, Math.round((progress.processed_rows / progress.row_count) * 100))
      : 0

  return (
    <Area>
      <AreaContent>
        <div className='mb-5'>
          <Text size={12} color={TextColor.SECONDARY} weight='bold'>
            マスタ種別
          </Text>
          <div className='mt-1.5'>
            <Selector
              name='masterType'
              value={masterType}
              onChange={(e) => setMasterType(e.target.value)}
              choices={CSV_FORMATS.map((f) => ({ name: f.label, value: f.type }))}
              size={SelectorSize.AUTO}
            />
          </div>
        </div>

        <div className='mb-5'>
          <Text size={12} color={TextColor.SECONDARY} weight='bold'>
            CSVファイル
          </Text>
          <input
            ref={fileRef}
            type='file'
            accept='.csv,.tsv,.txt'
            onChange={handleFileChange}
            className='hidden'
          />
          <div className='mt-1.5 flex items-center gap-3'>
            <Button color={ButtonColor.SECONDARY} onClick={() => fileRef.current?.click()}>
              ファイルを選択
            </Button>
            {fileName && (
              <Text size={12} color={TextColor.SECONDARY}>
                {fileName}
              </Text>
            )}
          </div>
        </div>

        {preview && (
          <div className='mb-5 overflow-x-auto'>
            <Text size={12} color={TextColor.SECONDARY}>
              プレビュー（{preview.totalLines} 行中 最初の {preview.rows.length} 行）
            </Text>
            <div className='mt-2'>
              <Table>
                <thead>
                  <TableRow head>
                    {preview.headers.map((h, i) => (
                      <TableHeadItem key={i}>
                        <Text>{h}</Text>
                      </TableHeadItem>
                    ))}
                  </TableRow>
                </thead>
                <tbody>
                  {preview.rows.map((row, ri) => (
                    <TableRow key={ri}>
                      {row.map((cell, ci) => (
                        <TableRowItem key={ci}>
                          <Text size={12}>{cell}</Text>
                        </TableRowItem>
                      ))}
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        <Button
          color={ButtonColor.PRIMARY}
          disabled={!fileName || uploading || !!progress}
          loading={uploading}
          onClick={handleUpload}
        >
          {progress ? '処理中...' : '取込み実行'}
        </Button>

        {progress && (
          <div className='mt-4'>
            <div className='mb-1.5 flex justify-between'>
              <Text size={12} color={TextColor.SECONDARY}>
                {progress.status === 'processing' ? '取込み処理中...' : '処理待ち...'}
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                {progress.row_count > 0
                  ? `${progress.processed_rows.toLocaleString()} / ${progress.row_count.toLocaleString()} 行`
                  : `${progress.processed_rows.toLocaleString()} 行処理済み`}
              </Text>
            </div>
            <div className='bg-fill-tertiary h-2 overflow-hidden rounded-full'>
              <div
                className='bg-fill-brand h-full rounded-full transition-all duration-500'
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {progress.row_count > 0 && (
              <Text size={10} color={TextColor.SECONDARY} align='right'>
                {progressPct}%
              </Text>
            )}
          </div>
        )}

        {result && !result.error && (
          <div className='mt-5'>
            <MessageBar
              type={result.status === 'failed' ? MessageType.CRITICAL : MessageType.SUCCESS}
              fillIn
            >
              {result.status === 'completed'
                ? '取込み完了'
                : result.status === 'failed'
                  ? '取込み失敗'
                  : '処理中...'}
            </MessageBar>
            <div className='mt-2 flex flex-wrap gap-4'>
              {result.row_count !== undefined && <Text size={12}>行数: {result.row_count}</Text>}
              {result.created_count !== undefined && (
                <Text size={12}>新規: {result.created_count}</Text>
              )}
              {result.updated_count !== undefined && (
                <Text size={12}>更新: {result.updated_count}</Text>
              )}
              {result.skipped_count !== undefined && (
                <Text size={12}>スキップ: {result.skipped_count}</Text>
              )}
              {/* 全件置き換えで削除した行数（add-w2-ex-product-spec で追加）。値が返るときだけ表示 */}
              {result.deleted_count !== undefined && (
                <Text size={12}>削除: {result.deleted_count}</Text>
              )}
              {(result.error_count ?? 0) > 0 && (
                <Text size={12} color={TextColor.CRITICAL}>
                  エラー: {result.error_count}
                </Text>
              )}
            </div>
            {result.error_details && result.error_details.length > 0 && (
              <div className='mt-3'>
                <Text size={12} color={TextColor.CRITICAL}>
                  エラー詳細:
                </Text>
                <div className='mt-1 flex flex-col gap-1'>
                  {result.error_details.slice(0, 10).map((e, i) => (
                    <Text key={i} size={12} color={TextColor.SECONDARY}>
                      行 {e.row}: {e.message}
                    </Text>
                  ))}
                  {result.error_details.length > 10 && (
                    <Text size={10} color={TextColor.SECONDARY}>
                      ... 他 {result.error_details.length - 10} 件
                    </Text>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {result?.error && (
          <div className='mt-5'>
            <MessageBar type={MessageType.CRITICAL} fillIn>
              {result.error}
            </MessageBar>
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

// ─── Error Detail Modal ───────────────────────────────────────────────────────

const ErrorDetailModal = ({
  siteId,
  log,
  onClose
}: {
  siteId: number
  log: UploadLog
  onClose: () => void
}) => {
  // error_details は一覧に含まれないため、モーダルを開いたタイミングで詳細取得する。
  const { data, isLoading } = useUploadLogDetailApi({ site_id: siteId, log_id: log.id })
  const errors = data?.error_details || []
  return (
    <div
      className='z-modal fixed inset-0 flex items-center justify-center bg-black/60'
      onClick={onClose}
    >
      <div
        className='bg-surface border-secondary shadow-area max-h-[80vh] w-11/12 max-w-xl overflow-y-auto rounded-xl border p-7'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='mb-4 flex items-center justify-between'>
          <Text size={14} weight='bold' color={TextColor.CRITICAL}>
            エラー詳細 — ID: {log.id} / {log.filename}
          </Text>
          <Button color={ButtonColor.TERTIARY} onClick={onClose}>
            ✕
          </Button>
        </div>
        {isLoading ? (
          <Text color={TextColor.SECONDARY}>読込中...</Text>
        ) : errors.length === 0 ? (
          <Text color={TextColor.SECONDARY}>エラー詳細がありません</Text>
        ) : (
          <div className='flex flex-col gap-2'>
            {errors.map((e, i) => (
              <div key={i} className='border-secondary bg-surface-secondary rounded-lg border p-3'>
                <Text size={12} weight='bold' color={TextColor.CRITICAL}>
                  行 {e.row}
                </Text>
                <Text size={12} color={TextColor.SECONDARY} preWrap>
                  {e.message}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Logs Tab ─────────────────────────────────────────────────────────────────

const LogsTab = ({ siteId }: { siteId: number }) => {
  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('')
  const [errorPopup, setErrorPopup] = useState<UploadLog | null>(null)

  const { data, isLoading, mutate } = useUploadLogsApi({
    site_id: siteId,
    page,
    per_page: 30,
    status: filterStatus || undefined,
    master_type: filterType || undefined
  })
  const { trigger: triggerRetry } = useRetryUploadApi()
  const logs = data?.logs || []
  const total = data?.total ?? 0

  const handleRetry = async (logId: number) => {
    try {
      await triggerRetry({ site_id: siteId, log_id: logId })
      setTimeout(() => mutate(), 2000)
    } catch {
      /* ignore */
    }
  }

  const totalPages = Math.ceil(total / 30)

  const statusOptions = [
    { value: '', label: '全ステータス' },
    { value: 'pending', label: '待機中' },
    { value: 'processing', label: '処理中' },
    { value: 'completed', label: '完了' },
    { value: 'failed', label: '失敗' }
  ]

  return (
    <Area tableWrap noBorder>
      <AreaContent>
        <div className='mb-4 flex flex-wrap gap-3'>
          <Selector
            name='filterType'
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value)
              setPage(1)
            }}
            choices={[
              { name: '全マスタ種別', value: '' },
              ...CSV_FORMATS.map((f) => ({ name: f.label, value: f.type }))
            ]}
            size={SelectorSize.AUTO}
          />
          <Selector
            name='filterStatus'
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value)
              setPage(1)
            }}
            choices={statusOptions.map((o) => ({ name: o.label, value: o.value }))}
            size={SelectorSize.AUTO}
          />
        </div>
      </AreaContent>
      <Table>
        <thead>
          <TableRow head>
            <TableHeadItem>
              <Text>ID</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>ファイル名</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>マスタ種別</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>ステータス</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>行数</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>新規</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>更新</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>スキップ</Text>
            </TableHeadItem>
            {/* 全件置き換えで削除した行数（add-w2-ex-product-spec で追加） */}
            <TableHeadItem>
              <Text>削除</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>エラー</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>取込元</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>日時</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>操作</Text>
            </TableHeadItem>
          </TableRow>
        </thead>
        <tbody>
          {isLoading ? (
            <TableRow>
              <TableRowItem>
                <Text color={TextColor.SECONDARY}>読込中...</Text>
              </TableRowItem>
            </TableRow>
          ) : logs.length === 0 ? (
            <TableRow>
              <TableRowItem>
                <Text color={TextColor.SECONDARY}>ログがありません</Text>
              </TableRowItem>
            </TableRow>
          ) : (
            logs.map((log) => {
              const mt = CSV_FORMATS.find((f) => f.type === log.master_type)
              return (
                <TableRow key={log.id}>
                  <TableRowItem>
                    <Text size={12}>{log.id}</Text>
                  </TableRowItem>
                  <TableRowItem width={200}>
                    <Text size={12} truncate>
                      {log.filename}
                    </Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{mt?.label || log.master_type}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Badge badgeType={statusToBadgeType(log.status)}>
                      {statusLabel(log.status)}
                    </Badge>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{log.row_count}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{log.created_count}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{log.updated_count}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{log.skipped_count}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    {/* 削除件数が無いログ（バックエンド反映前など）は他の件数列と同じく 0 で表示する */}
                    <Text size={12}>{log.deleted_count ?? 0}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <ItemAlign gapX={4}>
                      <Text
                        size={12}
                        color={log.error_count > 0 ? TextColor.CRITICAL : TextColor.PRIMARY}
                      >
                        {log.error_count}
                      </Text>
                      {log.error_count > 0 && (
                        <button
                          className='text-size-10 text-link cursor-pointer underline'
                          onClick={() => setErrorPopup(log)}
                        >
                          詳細
                        </button>
                      )}
                    </ItemAlign>
                  </TableRowItem>
                  <TableRowItem>
                    <Badge badgeType={sourceToBadgeType(log.upload_source)}>
                      {SOURCE_LABELS[log.upload_source] || log.upload_source}
                    </Badge>
                  </TableRowItem>
                  <TableRowItem width={160}>
                    <Text size={10} nowrap>
                      {log.uploaded_at ? new Date(log.uploaded_at).toLocaleString('ja-JP') : '-'}
                    </Text>
                  </TableRowItem>
                  <TableRowItem>
                    {(log.status === 'failed' || log.status === 'completed') && (
                      <Button color={ButtonColor.TERTIARY} onClick={() => handleRetry(log.id)}>
                        リトライ
                      </Button>
                    )}
                  </TableRowItem>
                </TableRow>
              )
            })
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <AreaContent>
          <div className='mt-4 flex justify-center gap-2'>
            <Button
              color={ButtonColor.TERTIARY}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ←
            </Button>
            <Text size={12} color={TextColor.SECONDARY} label>
              {page} / {totalPages}
            </Text>
            <Button
              color={ButtonColor.TERTIARY}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              →
            </Button>
          </div>
        </AreaContent>
      )}
      {errorPopup && (
        <ErrorDetailModal siteId={siteId} log={errorPopup} onClose={() => setErrorPopup(null)} />
      )}
    </Area>
  )
}

// ─── Format Guide Tab ─────────────────────────────────────────────────────────

const downloadSampleCsv = (format: CsvFormat) => {
  const bom = '﻿'
  const blob = new Blob([bom + format.sample], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${format.filePrefix}sample.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const FormatGuideTab = ({ siteId }: { siteId: number }) => {
  const [selected, setSelected] = useState<string>(CSV_FORMATS[0]?.type ?? 'orders')
  const visibleFormats = CSV_FORMATS

  const format = CSV_FORMATS.find((f) => f.type === selected)

  const commonSpecs = [
    { label: '文字コード', value: 'UTF-8（BOM付き可）/ Shift_JIS（自動判定）' },
    { label: '区切り文字', value: 'カンマ / タブ（自動判定）' },
    { label: '拡張子', value: '.csv / .tsv / .txt' },
    { label: 'ヘッダー行', value: '1行目に必須（カラム名を記載）' },
    { label: 'ファイルサイズ上限', value: '50MB' },
    {
      label: 'ファイル名規則',
      value: 'プレフィックスでマスタ種別を自動判定（例: products_20260228.csv）'
    }
  ]

  return (
    <Area>
      <AreaContent>
        <div className='mb-6'>
          <Text size={14} weight='bold'>
            共通仕様
          </Text>
          <div className='mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2'>
            {commonSpecs.map((spec) => (
              <div
                key={spec.label}
                className='bg-surface-secondary flex gap-2 rounded-lg px-3 py-2'
              >
                <Text size={12} color={TextColor.SECONDARY} nowrap>
                  {spec.label}
                </Text>
                <Text size={12}>{spec.value}</Text>
              </div>
            ))}
          </div>
        </div>

        <div className='mb-5'>
          <div className='mb-3 flex items-center gap-3'>
            <Text size={14} weight='bold'>
              マスタ種別ごとの仕様
            </Text>
            {/* 全サイトが W2Commerce 完全連動。連動していることは常に示す */}
            <Badge badgeType={BadgeType.EMPHASIS}>W2Commerce 完全連動</Badge>
          </div>
          <div className='flex flex-col gap-3'>
            {MASTER_GROUP_ORDER.map((group) => {
              const items = visibleFormats.filter((f) => masterTypeGroup(f.type) === group)
              if (items.length === 0) return null
              return (
                <div key={group}>
                  <Text color={TextColor.SECONDARY} weight='bold'>
                    {group}
                  </Text>
                  <div className='mt-1.5 flex flex-wrap gap-1.5'>
                    {items.map((f) => (
                      <button
                        key={f.type}
                        onClick={() => setSelected(f.type)}
                        className={clsx(
                          'text-size-12 rounded-lg border px-3 py-1.5',
                          selected === f.type
                            ? 'bg-fill-brand text-on-bg-fill border-fill-brand'
                            : 'bg-fill-tertiary text-primary border-secondary hover:bg-fill-on-bg-fill-hover'
                        )}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {format && (
          <div>
            <div className='mb-2 flex items-center gap-3'>
              <Text size={16} weight='bold'>
                {format.label}
              </Text>
              <Badge badgeType={BadgeType.EMPHASIS}>{format.behavior}</Badge>
            </div>
            <Text size={12} color={TextColor.SECONDARY}>
              {format.description}
            </Text>
            <div className='mt-1'>
              <Text size={12} color={TextColor.SECONDARY}>
                ファイル名:{' '}
                <code className='bg-fill-tertiary text-size-12 text-primary rounded px-1.5 py-0.5 font-mono'>
                  {format.filePrefix}YYYYMMDD.csv
                </code>
              </Text>
            </div>

            <div className='mt-4 overflow-x-auto'>
              <Table>
                <thead>
                  <TableRow head>
                    <TableHeadItem>
                      <Text>No</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text>カラム名</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text>必須</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text>説明</Text>
                    </TableHeadItem>
                  </TableRow>
                </thead>
                <tbody>
                  {format.columns.map((col, idx) => (
                    <TableRow key={col.name}>
                      <TableRowItem width={40}>
                        <Text size={10} color={TextColor.SECONDARY}>
                          {idx + 1}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <code className='bg-fill-tertiary text-size-12 text-primary rounded px-1.5 py-0.5 font-mono'>
                          {col.name}
                        </code>
                      </TableRowItem>
                      <TableRowItem width={64}>
                        {col.required ? (
                          <Badge badgeType={BadgeType.CRITICAL}>必須</Badge>
                        ) : (
                          <Text size={12} color={TextColor.SECONDARY}>
                            任意
                          </Text>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12} color={TextColor.SECONDARY}>
                          {col.desc}
                        </Text>
                      </TableRowItem>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className='mt-5'>
              <div className='mb-2 flex items-center justify-between'>
                <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                  サンプルデータ
                </Text>
                <Button color={ButtonColor.SECONDARY} onClick={() => downloadSampleCsv(format)}>
                  サンプルCSVをダウンロード
                </Button>
              </div>
              <div className='border-secondary bg-surface-secondary text-size-12 overflow-x-auto rounded-lg border p-3.5 font-mono leading-relaxed whitespace-pre'>
                {format.sample.split('\n').map((line, i) => (
                  <div key={i} className={i === 0 ? 'text-link' : 'text-secondary'}>
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

// ─── Relay Logs Tab ───────────────────────────────────────────────────────────

const RelayLogsTab = ({ siteId }: { siteId: number }) => {
  const [page, setPage] = useState(1)
  const { data, isLoading, mutate } = useRelayLogsApi({ site_id: siteId, page, per_page: 30 })
  const logs = data?.logs ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 30)

  return (
    <Area tableWrap noBorder>
      <AreaContent>
        <div className='mb-4 flex items-center justify-between'>
          <Text size={12} color={TextColor.SECONDARY}>
            VultrFTPリレーサーバーからの転送ステータス
          </Text>
          <Button color={ButtonColor.SECONDARY} onClick={() => mutate()}>
            更新
          </Button>
        </div>
      </AreaContent>
      <Table>
        <thead>
          <TableRow head>
            <TableHeadItem>
              <Text>ID</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>サイトID</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>ファイル名</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>ステータス</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>詳細</Text>
            </TableHeadItem>
            <TableHeadItem>
              <Text>日時</Text>
            </TableHeadItem>
          </TableRow>
        </thead>
        <tbody>
          {isLoading ? (
            <TableRow>
              <TableRowItem>
                <Text color={TextColor.SECONDARY}>読込中...</Text>
              </TableRowItem>
            </TableRow>
          ) : logs.length === 0 ? (
            <TableRow>
              <TableRowItem>
                <Text color={TextColor.SECONDARY}>ログがありません</Text>
              </TableRowItem>
            </TableRow>
          ) : (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableRowItem>
                  <Text size={12}>{log.id}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Text size={12}>{log.tenant_id}</Text>
                </TableRowItem>
                <TableRowItem width={200}>
                  <Text size={12} truncate>
                    {log.filename}
                  </Text>
                </TableRowItem>
                <TableRowItem>
                  <Badge badgeType={relayStatusToBadgeType(log.status)}>
                    {relayStatusLabel(log.status)}
                  </Badge>
                </TableRowItem>
                <TableRowItem width={200}>
                  <Text size={12} truncate color={TextColor.SECONDARY}>
                    {log.detail || '-'}
                  </Text>
                </TableRowItem>
                <TableRowItem width={160}>
                  <Text size={10} nowrap>
                    {log.created_at ? new Date(log.created_at).toLocaleString('ja-JP') : '-'}
                  </Text>
                </TableRowItem>
              </TableRow>
            ))
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <AreaContent>
          <div className='mt-4 flex justify-center gap-2'>
            <Button
              color={ButtonColor.TERTIARY}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ←
            </Button>
            <Text size={12} color={TextColor.SECONDARY} label>
              {page} / {totalPages}
            </Text>
            <Button
              color={ButtonColor.TERTIARY}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              →
            </Button>
          </div>
        </AreaContent>
      )}
    </Area>
  )
}

// ─── Tab Navigation ───────────────────────────────────────────────────────────

type TabKey = 'upload' | 'format' | 'logs' | 'relay'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'upload', label: '手動アップロード' },
  { key: 'format', label: 'CSVフォーマット' },
  { key: 'logs', label: '取込実行ログ' },
  { key: 'relay', label: 'FTP連携ログ' }
]

// ─── Main Page ────────────────────────────────────────────────────────────────

const FileUploadPage = () => {
  const siteId = useSiteId()
  const [tab, setTab] = useState<TabKey>('upload')
  const [showNotify, setShowNotify] = useState(false)
  const { data: siteData } = useSiteGetApi({ site_id: siteId })
  const ftpEnabled = siteData?.ftp_enabled ?? null

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='ファイル取込設定'
        badgeNode={<AppTitleBadge type='master' />}
        description='CSVファイルを手動アップロードしてマスタデータを取り込みます。'
        rightNode={
          <ItemAlign gapX={8}>
            <FtpEnabledBadge ftpEnabled={ftpEnabled} />
            {ftpEnabled && <RelayStatusBadge siteId={siteId} />}
          </ItemAlign>
        }
      />

      <Content>
        <div className='flex flex-wrap items-center gap-2'>
          {TABS.map((t) => (
            <Button
              key={t.key}
              onClick={() => setTab(t.key)}
              color={tab === t.key ? ButtonColor.PRIMARY : ButtonColor.TERTIARY}
              size='large'
            >
              {t.label}
            </Button>
          ))}
          <div className='ml-auto'>
            <Button
              color={showNotify ? ButtonColor.PRIMARY : ButtonColor.TERTIARY}
              onClick={() => setShowNotify((v) => !v)}
            >
              FTPメール通知設定
            </Button>
          </div>
        </div>

        {showNotify && (
          <NotificationSettingsPanel siteId={siteId} onClose={() => setShowNotify(false)} />
        )}

        {tab === 'upload' && <UploadTab siteId={siteId} />}
        {tab === 'format' && <FormatGuideTab siteId={siteId} />}
        {tab === 'logs' && <LogsTab siteId={siteId} />}
        {tab === 'relay' && <RelayLogsTab siteId={siteId} />}
      </Content>
    </Main>
  )
}

export default FileUploadPage
